import type { Rng } from './rng.js';

/**
 * Exact batch allocation. A ratio the operator sets on a slider is a promise
 * about the batch: "70% female" over 20 drafts means 14, not "about 14". Each
 * helper deals exact counts, then shuffles them with the seeded stream, so the
 * result is reproducible and the order carries no pattern.
 */

export function allocateGenders(count: number, femaleRatio: number, rng: Rng): ('male' | 'female')[] {
  return allocateFlags(count, femaleRatio, rng).map((f) => (f ? 'female' : 'male'));
}

/** Exactly `round(count * ratio)` trues, shuffled. */
export function allocateFlags(count: number, ratio: number, rng: Rng): boolean[] {
  const n = exactCount(count, ratio);
  return shuffle(
    Array.from({ length: count }, (_, i) => i < n),
    rng,
  );
}

/**
 * One conflict group (styles that cannot share a row): each key gets its own
 * exact count, the sets are disjoint, the rest is null. Throws when the group
 * asks for more than the whole batch — the UI clamps before it gets here.
 */
export function allocateExclusive<K extends string>(
  count: number,
  ratios: Partial<Record<K, number>>,
  rng: Rng,
): (K | null)[] {
  const entries = (Object.entries(ratios) as [K, number | undefined][]).filter(
    (e): e is [K, number] => typeof e[1] === 'number' && e[1] > 0,
  );
  const sum = entries.reduce((s, [, r]) => s + r, 0);
  if (sum > 1 + 1e-9) throw new Error(`Exclusive ratios sum to ${String(sum)}, more than 1`);

  const out: (K | null)[] = [];
  for (const [key, r] of entries) {
    const n = Math.min(exactCount(count, r), count - out.length);
    for (let i = 0; i < n; i++) out.push(key);
  }
  while (out.length < count) out.push(null);
  return shuffle(out, rng);
}

/** Ages 19-26 carry this share when the range reaches them. */
const PEAK_SHARE = 0.7;
const PEAK_MIN = 19;
const PEAK_MAX = 26;
const DAY_MS = 86_400_000;

/**
 * An age skewed toward the 19-26 peak of the apps' real users, and a birth
 * date that gives exactly that age on `today`.
 */
export function drawBirthDate(
  ageMin: number,
  ageMax: number,
  rng: Rng,
  today: Date,
): { birthDate: string; age: number } {
  const lo = Math.min(ageMin, ageMax);
  const hi = Math.max(ageMin, ageMax);
  const peakLo = Math.max(lo, PEAK_MIN);
  const peakHi = Math.min(hi, PEAK_MAX);
  const age =
    peakLo <= peakHi && rng.next() < PEAK_SHARE ? randInt(peakLo, peakHi, rng) : randInt(lo, hi, rng);

  // Born after (today − age − 1 years) and no later than (today − age years).
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  const latest = Date.UTC(y - age, m, d);
  const earliest = Date.UTC(y - age - 1, m, d) + DAY_MS;
  const days = Math.round((latest - earliest) / DAY_MS);
  const born = new Date(earliest + randInt(0, days, rng) * DAY_MS);
  return { birthDate: born.toISOString().slice(0, 10), age };
}

function exactCount(count: number, ratio: number): number {
  return Math.max(0, Math.min(count, Math.round(count * Math.min(1, Math.max(0, ratio)))));
}

function randInt(min: number, max: number, rng: Rng): number {
  return min + Math.floor(rng.next() * (max - min + 1));
}

/** Fisher–Yates over the seeded stream. */
function shuffle<T>(xs: T[], rng: Rng): T[] {
  for (let i = xs.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [xs[i], xs[j]] = [xs[j] as T, xs[i] as T];
  }
  return xs;
}
